import React, { useState } from 'react';
import { useZxing } from 'react-zxing';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { X, Camera, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './Button';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onScan, onClose }) => {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(true);

  const { ref } = useZxing({
    onDecodeResult(result) {
      console.log('Barcode decodificato:', result.getText());
      onScan(result.getText());
    },
    onError(err: unknown) {
      console.error('Errore scanner:', err);
      const error = err as Error;
      if (error.name === 'NotAllowedError') {
        setError('Permesso fotocamera negato. Abilita l\'accesso alla fotocamera nelle impostazioni del browser.');
      } else if (error.name === 'NotFoundError') {
        setError('Nessuna fotocamera trovata sul dispositivo.');
      } else {
        setError('Errore nell\'avvio della fotocamera. Riprova.');
      }
      setIsScanning(false);
    },
    constraints: {
      video: {
        facingMode: 'environment', // Fotocamera posteriore su mobile
      },
    },
    hints: new Map([
      [DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13, // Barcode bottiglie di vino
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.QR_CODE,
      ]],
    ]) as any,
    timeBetweenDecodingAttempts: 300,
  });

  // Quando il video si carica, nascondi il loader
  const handleVideoLoad = () => {
    setIsScanning(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Camera className="w-6 h-6 text-white" />
          <div>
            <h3 className="text-white font-semibold">Scansiona Barcode</h3>
            <p className="text-white/70 text-sm">Inquadra il codice a barre della bottiglia</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md">
          {/* Loading */}
          {isScanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="flex flex-col items-center gap-4 text-white">
                <Loader2 className="w-12 h-12 animate-spin" />
                <p className="text-lg">Avvio fotocamera...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error ? (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-6 text-white text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
              <p className="text-lg mb-4">{error}</p>
              <Button variant="outline" onClick={onClose}>
                Chiudi
              </Button>
            </div>
          ) : (
            <>
              {/* Video Scanner */}
              <div className="relative">
                <video
                  ref={ref as any}
                  onLoadedMetadata={handleVideoLoad}
                  className="w-full h-auto rounded-lg border-2 border-wine-purple shadow-2xl"
                  style={{ maxHeight: '70vh' }}
                />

                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative w-4/5 h-48">
                    {/* Angoli del frame di scansione */}
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-wine-purple"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-wine-purple"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-wine-purple"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-wine-purple"></div>

                    {/* Linea di scansione animata */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-wine-purple animate-scan-line"></div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="mt-4 text-center text-white/80 text-sm space-y-1">
                <p>Posiziona il barcode nel riquadro</p>
                <p>La scansione avviene automaticamente</p>
                <p className="text-xs text-white/60 mt-2">Supporta EAN-13, UPC, Code128, QR</p>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes scan-line {
          0% { top: 0; }
          50% { top: 100%; }
          100% { top: 0; }
        }
        .animate-scan-line {
          animation: scan-line 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
